/**
 * The persisted settings groups every flying route needs: the volume sliders,
 * the cockpit's REALISTIC INSTRUMENTS toggle, and the port-extension effects
 * (AD-012's PROPELLER EFFECT).
 *
 * A route that flies has to load each on mount, keep each in a signal, and
 * write each back on change — three identical lines apiece, in every such
 * route. `/duel` made that two routes, so it lives here once. Reading and
 * writing only; applying a change to a live scene stays with whoever owns the
 * scene, because that is the part that differs.
 */

import { createSignal, type Accessor } from "solid-js";
import {
  browserSettingsStore,
  DEFAULT_AUDIO_SETTINGS,
  loadAudioSettings,
  saveAudioSettings,
  type AudioSettings,
} from "../audio/settings.ts";
import {
  DEFAULT_COCKPIT_SETTINGS,
  loadCockpitSettings,
  saveCockpitSettings,
  type CockpitSettings,
} from "./cockpit-settings.ts";
import {
  DEFAULT_EFFECTS_SETTINGS,
  loadEffectsSettings,
  saveEffectsSettings,
  type EffectsSettings,
} from "./effects-settings.ts";
import {
  loadKillCamPlacement,
  saveKillCamPlacement,
  type KillCamPlacement,
} from "./killcam-layout.ts";
import {
  DEFAULT_STICK_SETTINGS,
  loadStickSettings,
  saveStickSettings,
  type StickSettings,
} from "./stick-settings.ts";
import { DEFAULT_BAIL_SETTINGS, loadBailSettings, saveBailSettings, type BailSettings } from "./bail-settings.ts";

export interface PersistedFlightSettings {
  readonly audio: Accessor<AudioSettings>;
  readonly cockpit: Accessor<CockpitSettings>;
  readonly effects: Accessor<EffectsSettings>;
  /** AD-007n change 4 — the keyboard-stick shaping (`stick-settings.ts`). */
  readonly stick: Accessor<StickSettings>;
  /** AD-021's BAIL OUT — whether the pilot may go over the side (`bail-settings.ts`). */
  readonly bail: Accessor<BailSettings>;
  /** Where the pilot dragged AD-015's kill-camera window, or `null` for its default corner. */
  readonly killCamPlacement: Accessor<KillCamPlacement | null>;
  /** Updates the signal and writes through to storage. */
  writeAudio(next: AudioSettings): void;
  writeCockpit(next: CockpitSettings): void;
  writeEffects(next: EffectsSettings): void;
  writeStick(next: StickSettings): void;
  writeBail(next: BailSettings): void;
  writeKillCamPlacement(next: KillCamPlacement): void;
  /**
   * The same signal **without** the storage write, for the frames of a drag
   * between pointer-down and pointer-up. Serialising JSON into `localStorage`
   * on every pointer move would be a synchronous string write at the pointer's
   * rate inside the flight loop; the release calls `writeKillCamPlacement`.
   */
  moveKillCamPlacement(next: KillCamPlacement): void;
}

export function createPersistedFlightSettings(): PersistedFlightSettings {
  const store = browserSettingsStore();
  const [audio, setAudio] = createSignal<AudioSettings>(store ? loadAudioSettings(store) : DEFAULT_AUDIO_SETTINGS);
  const [cockpit, setCockpit] = createSignal<CockpitSettings>(
    store ? loadCockpitSettings(store) : DEFAULT_COCKPIT_SETTINGS,
  );
  const [effects, setEffects] = createSignal<EffectsSettings>(
    store ? loadEffectsSettings(store) : DEFAULT_EFFECTS_SETTINGS,
  );
  const [stick, setStick] = createSignal<StickSettings>(store ? loadStickSettings(store) : DEFAULT_STICK_SETTINGS);
  const [bail, setBail] = createSignal<BailSettings>(store ? loadBailSettings(store) : DEFAULT_BAIL_SETTINGS);
  // A placement is a value, not a settings object, so the signal is written
  // with a thunk — Solid would otherwise call a bare object as an updater.
  const [killCamPlacement, setKillCamPlacement] = createSignal<KillCamPlacement | null>(
    store ? loadKillCamPlacement(store) : null,
  );
  return {
    audio,
    cockpit,
    effects,
    stick,
    bail,
    killCamPlacement,
    writeAudio(next) {
      setAudio(next);
      if (store) saveAudioSettings(store, next);
    },
    writeCockpit(next) {
      setCockpit(next);
      if (store) saveCockpitSettings(store, next);
    },
    writeEffects(next) {
      setEffects(next);
      if (store) saveEffectsSettings(store, next);
    },
    writeStick(next) {
      setStick(next);
      if (store) saveStickSettings(store, next);
    },
    writeBail(next) {
      setBail(next);
      if (store) saveBailSettings(store, next);
    },
    writeKillCamPlacement(next) {
      setKillCamPlacement(() => next);
      if (store) saveKillCamPlacement(store, next);
    },
    moveKillCamPlacement(next) {
      setKillCamPlacement(() => next);
    },
  };
}
